/**
 * Producer-facing HTTP contract: publish and the member presence read. Every
 * route takes a Keycloak service-account access token (client credentials) as
 * `Authorization: Bearer <token>`. The Worker verifies its signature against
 * the realm JWKS and its issuer, and requires `admin:is-service-token`
 * (`RELAY_SERVICE_TOKEN_SCOPE`) in the token's space-separated `scope` claim.
 * There is no shared secret. A member's access token never carries that scope,
 * so it cannot publish.
 *
 *   POST /v1/members/{userId}/events      RelayPublishRequest -> RelayPublishResponse
 *   GET  /v1/members/{userId}/presence    RelayMemberPresenceResponse
 *
 * Publish is a synchronous delivery attempt (TRD section 5). The member's
 * `MemberInbox` writes the frame to every live socket, whatever the topic, and
 * waits up to `RELAY_ACK_TIMEOUT_MS` for each client's `ack`. The response
 * says which surfaces acknowledged and whether the member's primary socket
 * did. For a storable topic the producer has already stored the envelope for
 * every surface before publishing, and now deletes the rows for `acked`
 * (`undelivered.ts`). For a claimable topic the producer reads `primary`: the
 * primary client is the one that will claim and display a Reset offer, so its
 * ack is what "dispatched" means. There is no event status route and no
 * idempotency window: the Relay remembers nothing after the call returns.
 *
 * There is no company-wide presence route. Presence is a per-member question
 * ("may this member be offered a Reset right now?") that the member's own
 * `MemberInbox` answers from the sockets it holds (TRD section 11); nothing is
 * stored to answer it.
 */
import { z } from "zod";
import { RelayTimestamp } from "./primitives.js";
import { RelaySurface } from "./topics.js";
/** The `scope` value that marks a Keycloak service-account token as a producer. */
export const RELAY_SERVICE_TOKEN_SCOPE = "admin:is-service-token";
/** Whether a token's space-separated `scope` claim names `RELAY_SERVICE_TOKEN_SCOPE`. */
export function relayScopeAllowsPublish(scope) {
    return typeof scope === "string" && scope.split(" ").includes(RELAY_SERVICE_TOKEN_SCOPE);
}
export const RelayMemberParams = z.strictObject({ userId: z.uuid() });
/**
 * How long `deliver()` waits for a client `ack` before reporting a surface
 * undelivered. Long enough for a laptop on a poor link, short enough that a
 * publisher in a request path is not held hostage. The Worker reads
 * `RELAY_ACK_TIMEOUT_MS` from its vars; this is the default and the test value.
 */
export const RELAY_ACK_TIMEOUT_MS = 2_000;
/**
 * Why no surface took delivery. `no_socket`: the inbox held no live, unexpired
 * socket. `ack_timeout`: at least one socket was sent the frame and none
 * acknowledged within the timeout (a half-open connection, a client
 * mid-reconnect).
 */
export const RelayUndeliveredReason = z.enum(["no_socket", "ack_timeout"]);
/**
 * The member's primary socket at the moment of the attempt: which surface it
 * is and whether it acknowledged. Exactly one live socket is primary (see
 * `frames.ts`), so this is null only when there was no live socket at all.
 */
export const RelayPrimaryAck = z.strictObject({
    client: RelaySurface,
    acked: z.boolean(),
});
/**
 * `acked` lists the surfaces whose client acknowledged the frame; the producer
 * deletes the rows it stored ahead of the attempt for exactly those surfaces,
 * and the rest stay for the client's catch-up fetch. `delivered` is true when
 * at least one surface acknowledged, and `reason` is set only when none did.
 * `primary` says whether the one client that will act on a claimable event
 * took it: a Reset offer is `dispatched` when `primary.acked`, whatever else
 * acknowledged.
 */
export const RelayPublishResponse = z
    .strictObject({
    delivered: z.boolean(),
    acked: z.array(RelaySurface),
    reason: RelayUndeliveredReason.nullable(),
    primary: RelayPrimaryAck.nullable(),
})
    .refine((value) => new Set(value.acked).size === value.acked.length, {
    message: "a surface acknowledges at most once",
    path: ["acked"],
})
    .refine((value) => value.delivered === value.acked.length > 0, {
    message: "delivered means at least one surface acknowledged",
    path: ["delivered"],
})
    .refine((value) => value.delivered === (value.reason === null), {
    message: "an undelivered publish names its reason, a delivered one has none",
    path: ["reason"],
})
    .refine((value) => (value.primary === null) === (value.reason === "no_socket"), {
    message: "a primary exists exactly when a live socket did",
    path: ["primary"],
})
    .refine((value) => !value.primary?.acked || value.acked.includes(value.primary.client), {
    message: "an acknowledging primary is among the acknowledged surfaces",
    path: ["primary"],
});
/**
 * How often a connected client sends the bare `ping` keepalive, and how long
 * after the last `pong` auto-response a socket still counts as present. Three
 * missed keepalives is absent.
 */
export const RELAY_KEEPALIVE_INTERVAL_SECONDS = 30;
export const RELAY_PRESENCE_WINDOW_SECONDS = 90;
/**
 * `present` is true when any of the member's live sockets (unexpired token,
 * any surface) answered a keepalive within the last
 * `RELAY_PRESENCE_WINDOW_SECONDS`.
 * `lastSeenAt` is that most recent sign of life, kept even when it is too old
 * to count, so an operator can tell "never connected" from "went quiet two
 * minutes ago". The only transport is the socket, so none is named.
 */
export const RelayMemberPresenceResponse = z
    .strictObject({
    present: z.boolean(),
    lastSeenAt: RelayTimestamp.nullable(),
})
    .refine((value) => !value.present || value.lastSeenAt !== null, {
    message: "a present member has a lastSeenAt",
    path: ["present"],
});
//# sourceMappingURL=producer.js.map